import { useMemo, useState } from 'react';
import { GitMerge, Trash2 } from 'lucide-react';

import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import type { LocationAliasMap, ManualGeoOverrideMap } from '@/features/network/models';

export function LocationAliasPanel({
  locations,
  aliases,
  onChange,
  geoOverrides,
  onGeoOverridesChange,
}: {
  locations: string[];
  aliases: LocationAliasMap;
  onChange: (next: LocationAliasMap) => void;
  geoOverrides: ManualGeoOverrideMap;
  onGeoOverridesChange: (next: ManualGeoOverrideMap) => void;
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [locationKey, setLocationKey] = useState('');
  const [latText, setLatText] = useState('');
  const [lonText, setLonText] = useState('');
  const [geoError, setGeoError] = useState<string | null>(null);
  const [geoSuccess, setGeoSuccess] = useState<string | null>(null);

  const aliasEntries = useMemo(() => Object.entries(aliases).sort(([a], [b]) => a.localeCompare(b)), [aliases]);
  const overrideEntries = useMemo(
    () => Object.entries(geoOverrides).sort(([a], [b]) => a.localeCompare(b)),
    [geoOverrides],
  );

  function addAlias() {
    const source = from.trim();
    const target = to.trim();
    if (!source || !target || source === target) return;
    onChange({ ...aliases, [source]: target });
    setFrom('');
    setTo('');
  }

  function normalizeLocationKey(value: string) {
    return value.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  function saveGeoOverride() {
    setGeoError(null);
    setGeoSuccess(null);

    const sourceLocation = locationKey.trim();
    if (!sourceLocation) {
      setGeoError('Укажи location key для override.');
      return;
    }

    const normalizedLocation = normalizeLocationKey(sourceLocation);
    const lat = Number(latText.trim().replace(',', '.'));
    const lon = Number(lonText.trim().replace(',', '.'));

    if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
      setGeoError('Широта и долгота должны быть числовыми значениями.');
      return;
    }

    if (lat < -90 || lat > 90) {
      setGeoError('lat должен быть в диапазоне от -90 до 90.');
      return;
    }

    if (lon < -180 || lon > 180) {
      setGeoError('lon должен быть в диапазоне от -180 до 180.');
      return;
    }

    onGeoOverridesChange({
      ...geoOverrides,
      [normalizedLocation]: {
        lat,
        lon,
        updatedAt: new Date().toISOString(),
      },
    });
    setGeoSuccess(`Сохранён override для "${normalizedLocation}".`);
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 text-lg font-semibold">
        <GitMerge className="h-5 w-5" />
        Alias / merge локаций
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Используй это, если одна и та же точка в Excel записана по-разному. Например, приведи обе записи к единому каноническому имени.
      </p>

      <datalist id="alias-locations">
        {locations.map((location) => <option key={location} value={location} />)}
      </datalist>

      <div className="mt-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
        <Input list="alias-locations" value={from} onChange={(event) => setFrom(event.target.value)} placeholder="Исходное имя" />
        <Input list="alias-locations" value={to} onChange={(event) => setTo(event.target.value)} placeholder="Каноническое имя" />
        <Button type="button" onClick={addAlias}>Склеить</Button>
      </div>

      <div className="mt-5 space-y-3">
        {aliasEntries.length === 0 ? <div className="text-sm text-slate-500 dark:text-slate-400">Пока нет правил объединения.</div> : null}
        {aliasEntries.map(([source, target]) => (
          <div key={source} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge>{source}</Badge>
              <span className="text-slate-400">→</span>
              <Badge className="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">{target}</Badge>
            </div>
            <Button type="button" variant="ghost" onClick={() => {
              const next = { ...aliases };
              delete next[source];
              onChange(next);
            }}>
              <Trash2 className="h-4 w-4" />
              Удалить
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-8 border-t border-slate-200 pt-6 dark:border-slate-800">
        <div className="text-base font-semibold">Ручной geo-override</div>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Укажи location key и координаты, чтобы вручную переопределить точку на карте. Этот override хранится локально и имеет приоритет над geocode cache.
        </p>

        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_180px_auto]">
          <Input
            list="alias-locations"
            value={locationKey}
            onChange={(event) => setLocationKey(event.target.value)}
            placeholder="Location key"
          />
          <Input value={latText} onChange={(event) => setLatText(event.target.value)} placeholder="lat (например, 55.7558)" />
          <Input value={lonText} onChange={(event) => setLonText(event.target.value)} placeholder="lon (например, 37.6176)" />
          <Button type="button" onClick={saveGeoOverride}>Сохранить override</Button>
        </div>

        {geoError ? <div className="mt-3 text-sm text-rose-600 dark:text-rose-300">{geoError}</div> : null}
        {geoSuccess ? <div className="mt-3 text-sm text-emerald-700 dark:text-emerald-300">{geoSuccess}</div> : null}

        <div className="mt-5 space-y-3">
          {overrideEntries.length === 0 ? <div className="text-sm text-slate-500 dark:text-slate-400">Пока нет сохранённых geo-override.</div> : null}
          {overrideEntries.map(([normalizedLocation, override]) => (
            <div key={normalizedLocation} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 p-3 dark:border-slate-800">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
                  {normalizedLocation}
                </Badge>
                <span className="text-slate-500 dark:text-slate-400">lat: {override.lat.toFixed(6)}</span>
                <span className="text-slate-500 dark:text-slate-400">lon: {override.lon.toFixed(6)}</span>
              </div>
              <Button type="button" variant="ghost" onClick={() => {
                const next = { ...geoOverrides };
                delete next[normalizedLocation];
                onGeoOverridesChange(next);
                setGeoSuccess(`Удалён override для "${normalizedLocation}".`);
                setGeoError(null);
              }}>
                <Trash2 className="h-4 w-4" />
                Удалить
              </Button>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
